import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaWhatsapp } from 'react-icons/fa';
import { CreditCard, Shield, Clock, CheckCircle } from 'lucide-react';
import TradeForm from '../components/TradeForm';
import PaymentForm from '../components/PaymentForm';

const features = [
  { icon: Clock, text: 'Quick Processing' },
  { icon: Shield, text: 'Secure Transactions' },
  { icon: CheckCircle, text: 'Verified Exchange' },
];

export default function TradePage() {
  const [activeTab, setActiveTab] = useState('paystack');

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 md:px-12 lg:px-24">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            <span className="text-text-primary">Trade </span>
            <span className="gold-gradient-text">Crypto Now</span>
          </h1>
          <p className="text-text-secondary text-lg max-w-xl mx-auto">
            Choose your preferred payment method to buy or sell cryptocurrency
          </p>

          {/* Features */}
          <div className="flex flex-wrap justify-center gap-6 mt-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.text}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="flex items-center gap-2 text-text-secondary"
              >
                <feature.icon size={18} className="text-primary-main" />
                <span>{feature.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Payment Method Tabs */}
        <div className="max-w-2xl mx-auto mb-6">
          <div className="flex gap-2 p-1 bg-background-paper rounded-full border border-border">
            <button
              onClick={() => setActiveTab('paystack')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-full font-medium transition-all ${
                activeTab === 'paystack'
                  ? 'bg-primary-main text-background shadow-lg'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              data-testid="tab-paystack"
            >
              <CreditCard size={18} />
              <span className="hidden sm:inline">Pay with Card</span>
              <span className="sm:hidden">Card</span>
            </button>
            <button
              onClick={() => setActiveTab('whatsapp')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-full font-medium transition-all ${
                activeTab === 'whatsapp'
                  ? 'bg-accent-success text-white shadow-lg'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              data-testid="tab-whatsapp"
            >
              <FaWhatsapp size={18} />
              <span className="hidden sm:inline">Trade via WhatsApp</span>
              <span className="sm:hidden">WhatsApp</span>
            </button>
          </div>
        </div>

        {/* Form Content */}
        <div className="max-w-2xl mx-auto">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'paystack' ? <PaymentForm /> : <TradeForm />}
          </motion.div>
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 glass-card p-6 max-w-2xl mx-auto"
        >
          <h3 className="font-heading text-lg font-semibold text-text-primary mb-4">
            How It Works
          </h3>
          {activeTab === 'paystack' ? (
            <ol className="space-y-3 text-text-secondary">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-primary-main/20 text-primary-main flex items-center justify-center text-sm font-semibold shrink-0">1</span>
                <span>Fill out your details and wallet address</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-primary-main/20 text-primary-main flex items-center justify-center text-sm font-semibold shrink-0">2</span>
                <span>Pay securely via Paystack (card, bank transfer, USSD)</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-primary-main/20 text-primary-main flex items-center justify-center text-sm font-semibold shrink-0">3</span>
                <span>Receive your crypto instantly to your wallet</span>
              </li>
            </ol>
          ) : (
            <ol className="space-y-3 text-text-secondary">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-primary-main/20 text-primary-main flex items-center justify-center text-sm font-semibold shrink-0">1</span>
                <span>Fill out the trade request form with your details</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-primary-main/20 text-primary-main flex items-center justify-center text-sm font-semibold shrink-0">2</span>
                <span>Submit the form and connect with us on WhatsApp</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-primary-main/20 text-primary-main flex items-center justify-center text-sm font-semibold shrink-0">3</span>
                <span>Complete the payment and receive your crypto instantly</span>
              </li>
            </ol>
          )}
        </motion.div>
      </div>
    </div>
  );
}
